import { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'react-qr-code';

import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import { ensureGalleryQr, getGalleryQr } from '@/services/galleryQr.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import SettingsHeading from '@/shared-components/text/components/SettingsHeading';
import Text from '@/shared-components/text/components/Text';

import {
  buildGalleryQrFilename,
  downloadQrPng,
  normalizeGalleryQrPayload,
} from './utils';

import './index.css';

export default function GalleryQrSettings() {
  const { state, stateHandler } = useStateHandler();
  const t = useTranslations('common.settings_menu.preferences.gallery_qr');
  const [qrData, setQrData] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const qrRef = useRef(null);

  const session = state.user_session;
  const hasQr = Boolean(qrData?.qrUrl);
  const displayUrl = useMemo(() => qrData?.qrUrl || '', [qrData]);

  useEffect(() => {
    let ignore = false;
    const existingQr = normalizeGalleryQrPayload(session);

    if (existingQr) {
      setQrData(existingQr);
      setCheckingExisting(false);
      return () => {
        ignore = true;
      };
    }

    setCheckingExisting(true);
    setError('');
    getGalleryQr()
      .then((loadedQr) => {
        if (ignore) {
          return;
        }
        setQrData(loadedQr);
        if (loadedQr?.token) {
          stateHandler.set('user_session.gallery_qr', {
            token: loadedQr.token,
            created_at: loadedQr.createdAt || loadedQr.created_at || null,
            created_by_gallery_id: loadedQr.createdByGalleryId || loadedQr.created_by_gallery_id || null,
          });
        }
      })
      .catch((loadError) => {
        if (ignore) {
          return;
        }
        setError(loadError?.message || t('errors.load'));
        setQrData(null);
      })
      .finally(() => {
        if (!ignore) {
          setCheckingExisting(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [session, stateHandler, t]);

  const qrSvgElement = () => qrRef.current?.querySelector('svg') ?? null;

  async function handleGenerate() {
    if (loading) {
      return;
    }

    setError('');
    setLoading(true);
    try {
      const nextQrData = await ensureGalleryQr();
      setQrData(nextQrData);
      if (nextQrData?.token) {
        stateHandler.set('user_session.gallery_qr', {
          token: nextQrData.token,
          created_at: nextQrData.createdAt || nextQrData.created_at || null,
          created_by_gallery_id: nextQrData.createdByGalleryId || nextQrData.created_by_gallery_id || null,
        });
      }
    } catch (generateError) {
      setError(generateError?.message || t('errors.generate'));
    } finally {
      setLoading(false);
    }
  }

  async function handleDownloadPng() {
    const svg = qrSvgElement();
    if (!svg || downloading) {
      return;
    }

    setDownloading(true);
    setError('');
    try {
      await downloadQrPng(svg, buildGalleryQrFilename(session, 'png'));
    } catch (downloadError) {
      setError(downloadError?.message || t('errors.download'));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      <SettingsHeading text={t('heading')} />

      <div className="settings-gallery-qr__container">
        {checkingExisting ? (
          <div className="settings-gallery-qr__state">
            <ContinueButton
              className="settings-gallery-qr__button"
              loading
              turnOff
            >
              {t('checking')}
            </ContinueButton>
          </div>
        ) : !hasQr ? (
          <div className="settings-gallery-qr__state">
            <Text className="settings-gallery-qr__copy " paragraph>
              {t('intro')}
            </Text>
            <ContinueButton
              className="settings-gallery-qr__button"
              loading={loading}
              onClick={handleGenerate}
            >
              {t('generate')}
            </ContinueButton>
          </div>
        ) : (
          <div className="settings-gallery-qr__preview">
            <div className="settings-gallery-qr__qr-frame" ref={qrRef}>
              <QRCode
                className="settings-gallery-qr__qr"
                value={displayUrl}
                size={240}
                level="M"
              />
            </div>

            <div className="settings-gallery-qr__meta">
              {qrData?.isActive === false && (
                <Text className="settings-gallery-qr__warning font-size-small" paragraph>
                  {t('inactive_warning')}
                </Text>
              )}
              <Text className="settings-gallery-qr__url font-size-small" paragraph>
                {displayUrl}
              </Text>
            </div>

            <ContinueButton
              className="settings-gallery-qr__button"
              loading={downloading}
              onClick={handleDownloadPng}
            >
              {t('download_png')}
            </ContinueButton>
          </div>
        )}

        {error && (
          <Text className="settings-gallery-qr__error font-size-small" paragraph>
            {error}
          </Text>
        )}
      </div>
    </>
  );
}
